<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useI18n } from "vue-i18n";
import { resolveThemeAsset } from "@/lib/theme-assets";

const { t } = useI18n();

interface ThemeZone {
  filename: string;
  nameKey: string;
}

interface Theme {
  slug: string;
  i18nKey: string;
  overviewScene: string;
  zones: ThemeZone[];
}

const themes: Theme[] = [
  {
    slug: "island",
    i18nKey: "island",
    overviewScene: "overview-base.webp",
    zones: [
      { filename: "beach-base.webp", nameKey: "beach" },
      { filename: "village-base.webp", nameKey: "village" },
      { filename: "jungle-base.webp", nameKey: "jungle" },
      { filename: "hilltop-base.webp", nameKey: "hilltop" },
      { filename: "cave-base.webp", nameKey: "cave" },
    ],
  },
  {
    slug: "pirate",
    i18nKey: "pirate",
    overviewScene: "overview-map.webp",
    zones: [
      { filename: "crows-nest-morning.webp", nameKey: "crowsNest" },
      { filename: "main-deck-noon.webp", nameKey: "mainDeck" },
      { filename: "captains-cabin-afternoon.webp", nameKey: "captainsCabin" },
      { filename: "stern-deck-evening.webp", nameKey: "sternDeck" },
      { filename: "below-deck-night.webp", nameKey: "belowDeck" },
    ],
  },
  {
    slug: "science-lab",
    i18nKey: "scienceLab",
    overviewScene: "overview-midday.webp",
    zones: [
      { filename: "greenhouse-morning.webp", nameKey: "greenhouse" },
      { filename: "main-lab-noon.webp", nameKey: "mainLab" },
      { filename: "workshop-afternoon.webp", nameKey: "workshop" },
      { filename: "observatory-evening.webp", nameKey: "observatory" },
      { filename: "supply-closet-night.webp", nameKey: "supplyCloset" },
    ],
  },
  {
    slug: "unicorn",
    i18nKey: "unicorn",
    overviewScene: "overview-map.webp",
    zones: [
      { filename: "dawn-meadow-morning.webp", nameKey: "dawnMeadow" },
      { filename: "rainbow-plaza-noon.webp", nameKey: "rainbowPlaza" },
      { filename: "candy-garden-afternoon.webp", nameKey: "candyGarden" },
      { filename: "sunset-balcony-evening.webp", nameKey: "sunsetBalcony" },
      { filename: "unicorn-burrow-night.webp", nameKey: "unicornBurrow" },
    ],
  },
  {
    slug: "wizards",
    i18nKey: "wizards",
    overviewScene: "overview-map.webp",
    zones: [
      { filename: "great-hall-morning.webp", nameKey: "greatHall" },
      { filename: "main-corridor-noon.webp", nameKey: "mainCorridor" },
      { filename: "practice-grounds-afternoon.webp", nameKey: "practiceGrounds" },
      { filename: "potions-classroom-evening.webp", nameKey: "potionsClassroom" },
      { filename: "library-loft-night.webp", nameKey: "libraryLoft" },
    ],
  },
];

const activeIndex = ref(0);
const activeTheme = computed(() => themes[activeIndex.value]!);

function goTo(index: number) {
  activeIndex.value = index;
  pauseAutoAdvance();
}

function goNext() {
  activeIndex.value = (activeIndex.value + 1) % themes.length;
  pauseAutoAdvance();
}

function goPrev() {
  activeIndex.value = (activeIndex.value + themes.length - 1) % themes.length;
  pauseAutoAdvance();
}

// --- Auto-advance ---
const AUTO_INTERVAL = 6000;
const IDLE_RESUME_DELAY = 10000;

let autoTimer: ReturnType<typeof setInterval> | null = null;
let resumeTimer: ReturnType<typeof setTimeout> | null = null;
const isAutoPlaying = ref(true);
const sectionRef = ref<HTMLElement | null>(null);
const isSectionVisible = ref(false);
const isPrefersReducedMotion = ref(false);

function startAutoAdvance() {
  stopAutoAdvance();
  if (isPrefersReducedMotion.value || !isSectionVisible.value) return;
  autoTimer = setInterval(() => {
    activeIndex.value = (activeIndex.value + 1) % themes.length;
  }, AUTO_INTERVAL);
  isAutoPlaying.value = true;
}

function stopAutoAdvance() {
  if (autoTimer !== null) {
    clearInterval(autoTimer);
    autoTimer = null;
  }
  isAutoPlaying.value = false;
}

function pauseAutoAdvance() {
  stopAutoAdvance();
  clearResumeTimer();
  resumeTimer = setTimeout(() => {
    startAutoAdvance();
  }, IDLE_RESUME_DELAY);
}

function clearResumeTimer() {
  if (resumeTimer !== null) {
    clearTimeout(resumeTimer);
    resumeTimer = null;
  }
}

function handleFocusIn() {
  stopAutoAdvance();
  clearResumeTimer();
}

function handleFocusOut() {
  pauseAutoAdvance();
}

// --- Swipe detection ---
let pointerStartX = 0;
let pointerStartY = 0;
let isSwiping = false;
const SWIPE_THRESHOLD = 50;

function handlePointerDown(e: PointerEvent) {
  pointerStartX = e.clientX;
  pointerStartY = e.clientY;
  isSwiping = true;
}

function handlePointerMove(e: PointerEvent) {
  if (!isSwiping) return;
  const dx = e.clientX - pointerStartX;
  if (Math.abs(dx) > 10) {
    e.preventDefault();
  }
}

function handlePointerUp(e: PointerEvent) {
  if (!isSwiping) return;
  isSwiping = false;
  const dx = e.clientX - pointerStartX;
  const dy = e.clientY - pointerStartY;
  if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
    if (dx < 0) goNext();
    else goPrev();
  }
}

// --- Keyboard navigation ---
function handleKeydown(e: KeyboardEvent) {
  if (e.key === "ArrowRight") {
    e.preventDefault();
    goNext();
  } else if (e.key === "ArrowLeft") {
    e.preventDefault();
    goPrev();
  }
}

// --- Visibility observer ---
let visibilityObserver: IntersectionObserver | null = null;

function handleVisibilityChange() {
  if (document.hidden) {
    stopAutoAdvance();
    clearResumeTimer();
  } else if (isSectionVisible.value) {
    startAutoAdvance();
  }
}

onMounted(() => {
  isPrefersReducedMotion.value = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
  const motionHandler = (e: MediaQueryListEvent) => {
    isPrefersReducedMotion.value = e.matches;
    if (e.matches) stopAutoAdvance();
    else if (isSectionVisible.value) startAutoAdvance();
  };
  mql.addEventListener("change", motionHandler);

  if (sectionRef.value) {
    visibilityObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) isSectionVisible.value = entry.isIntersecting;
      },
      { threshold: 0.2 },
    );
    visibilityObserver.observe(sectionRef.value);
  }

  document.addEventListener("visibilitychange", handleVisibilityChange);
});

watch(isSectionVisible, (visible) => {
  if (visible && !isPrefersReducedMotion.value) {
    startAutoAdvance();
  } else {
    stopAutoAdvance();
    clearResumeTimer();
  }
});

onBeforeUnmount(() => {
  stopAutoAdvance();
  clearResumeTimer();
  visibilityObserver?.disconnect();
  document.removeEventListener("visibilitychange", handleVisibilityChange);
});
</script>

<template>
  <section
    id="themes"
    ref="sectionRef"
    class="bg-[var(--color-bg-primary)] px-5 py-12 md:px-12 md:py-24"
    aria-roledescription="carousel"
    :aria-label="t('themes.carouselLabel')"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
    @keydown="handleKeydown"
  >
    <div class="mx-auto max-w-[var(--max-w-page)]">
      <!-- Section header -->
      <p
        data-reveal
        class="fade-up mb-3 text-center text-xs font-medium uppercase tracking-widest text-[var(--color-text-muted)] md:text-sm"
      >
        {{ t("themes.sectionLabel") }}
      </p>
      <h2
        data-reveal
        data-reveal-delay="1"
        class="fade-up mx-auto mb-10 max-w-[var(--max-w-wide)] text-center text-2xl font-medium text-[var(--color-text-primary)] md:mb-14 md:text-4xl"
        style="font-family: var(--font-display)"
      >
        {{ t("themes.heading") }}
      </h2>

      <!-- Hero image -->
      <div
        data-reveal
        data-reveal-delay="2"
        class="fade-up-lg hero-container relative mx-auto overflow-hidden rounded-2xl"
        tabindex="0"
        @pointerdown="handlePointerDown"
        @pointermove="handlePointerMove"
        @pointerup="handlePointerUp"
      >
        <div class="hero-image-wrapper" aria-live="polite">
          <img
            v-for="(theme, i) in themes"
            :key="theme.slug"
            :src="resolveThemeAsset(theme.slug, theme.overviewScene)"
            :alt="t(`themes.${theme.i18nKey}.name`)"
            class="hero-image"
            :class="{ 'is-active': i === activeIndex }"
            :loading="i === 0 ? 'eager' : 'lazy'"
            :fetchpriority="i === 0 ? 'high' : 'auto'"
            decoding="async"
            draggable="false"
          />
        </div>

        <!-- Frosted glass overlay -->
        <div class="hero-overlay">
          <p class="hero-overlay__name">{{ t(`themes.${activeTheme.i18nKey}.name`) }}</p>
          <p class="hero-overlay__tagline">{{ t(`themes.${activeTheme.i18nKey}.tagline`) }}</p>
        </div>
      </div>

      <!-- Zone thumbnails -->
      <div class="zone-strip" data-reveal data-reveal-delay="3">
        <div class="zone-strip__track">
          <img
            v-for="zone in activeTheme.zones"
            :key="`${activeTheme.slug}-${zone.filename}`"
            :src="resolveThemeAsset(activeTheme.slug, zone.filename)"
            :alt="t(`themes.${activeTheme.i18nKey}.zones.${zone.nameKey}`)"
            class="zone-strip__thumb"
            loading="lazy"
            decoding="async"
            draggable="false"
          />
        </div>
      </div>

      <!-- Navigation dots + arrows -->
      <nav
        class="mt-6 flex items-center justify-center gap-3 md:mt-8"
        :aria-label="t('themes.themeNavLabel')"
        data-reveal
        data-reveal-delay="4"
      >
        <button
          class="nav-arrow hidden md:flex"
          :aria-label="t('themes.prevTheme')"
          @click="goPrev"
        >
          <UIcon name="i-lucide-arrow-left" class="size-4" />
        </button>

        <div class="flex items-center gap-2">
          <button
            v-for="(theme, i) in themes"
            :key="theme.slug"
            class="nav-dot"
            :class="{ 'is-active': i === activeIndex }"
            :aria-label="t('themes.goToTheme', { name: t(`themes.${theme.i18nKey}.name`) })"
            :aria-pressed="i === activeIndex"
            @click="goTo(i)"
          />
        </div>

        <button
          class="nav-arrow hidden md:flex"
          :aria-label="t('themes.nextTheme')"
          @click="goNext"
        >
          <UIcon name="i-lucide-arrow-right" class="size-4" />
        </button>
      </nav>
    </div>
  </section>
</template>

<style scoped>
/* Hero image area */
.hero-container {
  aspect-ratio: 16 / 9;
  touch-action: pan-y pinch-zoom;
}

.hero-image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.hero-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  pointer-events: none;
}

.hero-image.is-active {
  opacity: 1;
  pointer-events: auto;
}

@media (prefers-reduced-motion: no-preference) {
  .hero-image {
    transition: opacity 500ms var(--ease-smooth);
  }
}

/* Frosted glass overlay */
.hero-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 20px;
  background: rgba(16, 26, 18, 0.55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

@media (min-width: 768px) {
  .hero-overlay {
    padding: 20px 28px;
  }
}

.hero-overlay__name {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 16px;
  color: #fff;
  line-height: 1.3;
}

@media (min-width: 768px) {
  .hero-overlay__name {
    font-size: 20px;
  }
}

.hero-overlay__tagline {
  font-family: var(--font-body);
  font-weight: 400;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2px;
  line-height: 1.4;
}

@media (min-width: 768px) {
  .hero-overlay__tagline {
    font-size: 15px;
  }
}

/* Zone thumbnail strip */
.zone-strip {
  margin-top: 16px;
}

@media (min-width: 768px) {
  .zone-strip {
    margin-top: 20px;
  }
}

.zone-strip__track {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
}

@media (min-width: 768px) {
  .zone-strip__track {
    justify-content: center;
    gap: 12px;
    overflow-x: visible;
  }
}

.zone-strip__thumb {
  flex: 0 0 120px;
  width: 120px;
  aspect-ratio: 3 / 2;
  object-fit: cover;
  border-radius: 12px;
  border: 1px solid var(--color-border);
  scroll-snap-align: start;
}

@media (min-width: 768px) {
  .zone-strip__thumb {
    flex: 0 0 140px;
    width: 140px;
    border-radius: 14px;
  }
}

/* Navigation dots */
.nav-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-text-muted);
  opacity: 0.4;
  border: none;
  padding: 0;
  cursor: pointer;
}

.nav-dot.is-active {
  opacity: 1;
  background: var(--color-primary);
  width: 10px;
  height: 10px;
}

@media (prefers-reduced-motion: no-preference) {
  .nav-dot {
    transition:
      opacity 200ms var(--ease-smooth),
      width 200ms var(--ease-smooth),
      height 200ms var(--ease-smooth),
      background 200ms var(--ease-smooth);
  }
}

/* Arrow buttons */
.nav-arrow {
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0;
}

@media (prefers-reduced-motion: no-preference) {
  .nav-arrow {
    transition:
      border-color 150ms ease,
      color 150ms ease;
  }
}

.nav-arrow:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}
</style>
